import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getAllOrgs } from "../../../api/AffiliateApi";
import { updatePersonnelStatus } from "../../../api/PersonnelApi";
import { PersonnelTypes } from "../../../constants/PersonnelTypes";
import { useAffiliation } from "../../../contexts/AffiliationContext";
import { useAlert } from "../../../contexts/AlertsContext";
import { BigsSelect } from "../BigsBcrWrapper";
import { RequiredLabel } from "../FormElements";
import { OutlinedButtonStyled } from "../OutlinedButtonStyled";
import { PrimaryButtonStyled } from "../PrimaryButtonStyled";

type ReactivatePersonnelBodyProps = {
    personnel: any;
    callback: Function;
    selectedOrg: any;
    setSelectedOrg: Function;
    selectedAffiliation: any;
    setSelectedAffiliation: Function;
};

const BodyContent = styled.div.attrs(() => ({
    className: "mt-4 ml-4",
}))`
    text-align: left;
    margin-bottom: 200px;
`;

const TopTextStyle = styled.div.attrs(() => ({}))`
    &&& {
        font-size: 16px;
        color: var(--slate);
        line-height: 24px;
        text-style: Helvetica;
    }
`;

const BottomTextStyle = styled.div.attrs(() => ({}))`
    &&& {
        font-size: 12px;
        text-style: Helvetica;
        font-weight: 300;
        width: 400px;
    }
`;

const ReactivatePersonnelBody: React.FC<ReactivatePersonnelBodyProps> = ({
    personnel,
    selectedOrg,
    setSelectedOrg,
    setSelectedAffiliation,
    selectedAffiliation,
}) => {
    const { affiliationDropdownOptions, refreshAffiliationDropdownByOrgId } = useAffiliation();
    const [orgDropdownOptions, setOrgDropdownOptions] = useState<any[]>([]);

    const personnelType = personnel?.personnelType?.type;

    useEffect(() => {
        const orgType = personnelType === PersonnelTypes.UMPIRE ? "Umpire" : "Club";
        getAllOrgs(orgType).then(response => {
            const safeList = (Array.isArray(response) ? response : []).filter(
                (org: any) => org?.orgId != null && org?.name,
            );
            const orgOptions = safeList
                .map((org: any) => ({
                    value: org.id,
                    label: org.name,
                    teamId: org.teamId,
                    orgId: org.orgId,
                }))
                .sort((a: any, b: any) => a.label.localeCompare(b.label));
            setOrgDropdownOptions(orgOptions);
        });
    }, [personnelType]);

    const orgChangeHandler = (orgOption: any) => {
        setSelectedOrg(orgOption);
        setSelectedAffiliation(null); // Clear team selection when org changes
        refreshAffiliationDropdownByOrgId(orgOption.orgId); // Load teams for this org
    };

    const affiliationChangeHandler = (affiliationOption: any) => {
        setSelectedAffiliation(affiliationOption);
    };

    return (
        <BodyContent>
            <TopTextStyle>
                Add {personnel.lastName}, {personnel.firstName} to the following Roster.
            </TopTextStyle>
            <div className="mr-1 mt-2">
                <RequiredLabel text="Organization" required />
                <BottomTextStyle>
                    <BigsSelect
                        value={selectedOrg}
                        isDisabled={false}
                        onChange={(orgOpt: any) => {
                            orgChangeHandler(orgOpt);
                        }}
                        options={orgDropdownOptions}
                        placeholder="select..."
                    />
                </BottomTextStyle>
            </div>
            <div className="mr-1 mt-2">
                <RequiredLabel text="Team" required />
                <BottomTextStyle>
                    <BigsSelect
                        value={selectedAffiliation}
                        isDisabled={!selectedOrg}
                        onChange={(affiliationOpt: any) => {
                            affiliationChangeHandler(affiliationOpt);
                        }}
                        options={affiliationDropdownOptions}
                        placeholder="select..."
                    />
                </BottomTextStyle>
            </div>
        </BodyContent>
    );
};

type ReactivatePersonnelFooterProps = {
    personnel: {
        id: number;
        status: string;
        affiliation: any;
    };
    closeModal: Function;
    callback: Function;
    selectedOrg: any;
    selectedAffiliation: any;
};

const ReactivatePersonnelFooter: React.FC<ReactivatePersonnelFooterProps> = ({
    personnel,
    closeModal,
    callback,
    selectedOrg,
    selectedAffiliation,
}) => {
    const showAlert = useAlert();
    const addPersonnelHandler = () => {
        updatePersonnelStatus(personnel.id, "ACTIVE", selectedAffiliation.teamId)
            .then(() => {
                if (callback && typeof callback === "function") {
                    callback(selectedAffiliation);
                }
                showAlert("Added to Roster");
            })
            .catch(() => {
                showAlert("Error adding personnel to roster", "danger");
            });
    };

    return (
        <div className="d-flex">
            <PrimaryButtonStyled
                height={26}
                width={180}
                disabled={!selectedOrg || !selectedAffiliation}
                onClick={event => {
                    event.stopPropagation();
                    closeModal();
                    addPersonnelHandler();
                }}
            >
                Add to Roster
            </PrimaryButtonStyled>
            <div className="pl-2">
                <OutlinedButtonStyled
                    width={80}
                    height={26}
                    disabled={false}
                    onClick={event => {
                        event.stopPropagation();
                        closeModal();
                    }}
                >
                    Cancel
                </OutlinedButtonStyled>
            </div>
        </div>
    );
};

ReactivatePersonnelBody.displayName = "BodyContent";
ReactivatePersonnelFooter.displayName = "FooterContent";

export { ReactivatePersonnelBody, ReactivatePersonnelFooter };
